/**
 * Agrupa versiones de un mismo archivo (evidencia/audio) enlazadas por un puntero
 * `reemplazaXId` en una cadena ordenada. Cada reemplazo es una fila nueva en la base
 * de datos (borrado lógico: la fila anterior no se borra, solo deja de ser la vigente),
 * así que sin esta agrupación cada versión aparecería como una tarjeta separada.
 */
export interface CadenaArchivo<T> {
  /** Versión vigente (la más reciente) — sobre la que actúan los botones de revisión. */
  actual: T;
  /** Primera versión subida en la cadena. */
  inicial: T;
  /** Cadena completa ordenada de más antigua a más reciente (incluye inicial y actual). */
  version: T[];
  /** id de la versión reemplazada → versión que la reemplazó. Para asociar el evento
   * "se subió una nueva versión" (keyed por el id viejo) con el archivo nuevo. */
  siguientePorId: Map<number, T>;
}

export function agruparCadenaArchivos<T>(
  items: T[],
  getId: (item: T) => number,
  getReemplazaId: (item: T) => number | null | undefined
): CadenaArchivo<T>[] {
  const porId = new Map(items.map((item) => [getId(item), item]));
  const referenciados = new Set<number>();
  for (const item of items) {
    const reemplazaId = getReemplazaId(item);
    if (reemplazaId != null) referenciados.add(reemplazaId);
  }

  // Las "hojas" son versiones que nadie más reemplaza — son las vigentes de cada cadena.
  const hojas = items.filter((item) => !referenciados.has(getId(item)));

  return hojas.map((hoja) => {
    const version: T[] = [hoja];
    let actual = hoja;
    for (;;) {
      const reemplazaId = getReemplazaId(actual);
      if (reemplazaId == null) break;
      const anterior = porId.get(reemplazaId);
      if (!anterior) break;
      version.push(anterior);
      actual = anterior;
    }
    version.reverse();

    const siguientePorId = new Map<number, T>();
    for (let i = 0; i < version.length - 1; i++) {
      siguientePorId.set(getId(version[i]!), version[i + 1]!);
    }

    return { actual: hoja, inicial: version[0]!, version, siguientePorId };
  });
}
